import React from 'react';
import { useTimezone } from '@app/contexts/useTimezone';
import { getBaselineComparisonColor, getBaselineComparisonTextColor } from './utils';
import { formatTimestepForDisplay, getValuesForTooltip } from './BaselineComparisonView.utils';

interface BaselineComparisonHeatmapTableProps {
  comparisonData: Array<{
    modelLabel: string;
    timestepDifferences: { [timestep: string]: number | null };
  }>;
  timesteps: string[];
  baselineLabel: string;
  aggregationMode: 'hourly' | 'daily';
  alignmentMode?: 'native' | 'common';
  chartData: Array<{ time: string; [key: string]: any }>;
  onCellHover: (
    e: React.MouseEvent,
    modelLabel: string,
    timestep: string,
    difference: number,
    actualValue: number,
    baselineValue: number,
  ) => void;
  onCellLeave: () => void;
  statisticalContext?: { std: number; thresholds: [number, number, number] };
}

const BaselineComparisonHeatmapTable: React.FC<BaselineComparisonHeatmapTableProps> = ({
  comparisonData,
  timesteps,
  baselineLabel,
  aggregationMode,
  alignmentMode = 'native',
  chartData,
  onCellHover,
  onCellLeave,
  statisticalContext,
}) => {
  const { timezone, formatInUserTimezone } = useTimezone();
  // Optionally filter to common timesteps where all models have values
  const filteredTimesteps = React.useMemo(() => {
    if (alignmentMode !== 'common') return timesteps;
    return timesteps.filter((t) =>
      comparisonData.every(
        (row) => row.timestepDifferences[t] !== null && row.timestepDifferences[t] !== undefined,
      ),
    );
  }, [alignmentMode, timesteps, comparisonData]);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="flex">
        {/* Sticky column for model labels */}
        <div className="w-40 flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 z-10">
          {/* Sticky header for model labels */}
          <div className="bg-gray-50 dark:bg-gray-800 px-2 py-1 h-10 flex items-center border-b border-gray-200 dark:border-gray-700">
            <div className="text-xs text-gray-600 dark:text-gray-300 font-medium">Models</div>
          </div>

          {/* Model labels */}
          {comparisonData.map((rowData, index) => (
            <div
              key={rowData.modelLabel}
              className={`flex items-center px-2 border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${index % 2 === 0 ? 'bg-gray-25 dark:bg-gray-850' : 'bg-white dark:bg-gray-900'}`}
              style={{ height: '40px' }}
            >
              <div className="text-xs text-gray-600 dark:text-gray-300 font-medium leading-tight">
                {rowData.modelLabel.includes(' | ') ? (
                  rowData.modelLabel.split(' | ').map((part, partIndex) => (
                    <div key={partIndex} className="break-words">
                      {part}
                    </div>
                  ))
                ) : (
                  <div className="break-words">{rowData.modelLabel}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Scrollable content area (header + data together) */}
        <div
          className="flex-1 overflow-x-auto overflow-y-visible pb-4 pr-4"
          style={{ scrollbarGutter: 'stable' }}
        >
          <div className="min-w-max">
            {/* Column headers (Timesteps) */}
            <div className="flex items-center gap-0.5 bg-gray-50 dark:bg-gray-800 px-2 py-1 border-b border-gray-200 dark:border-gray-700 h-10">
              {filteredTimesteps.map((timestep) => {
                const formatted = formatTimestepForDisplay(
                  timestep,
                  aggregationMode,
                  timezone,
                  formatInUserTimezone,
                );
                const columnWidth = '50px';

                return (
                  <div
                    key={timestep}
                    className="flex-none text-center h-full flex items-center justify-center"
                    style={{ minWidth: columnWidth, width: columnWidth }}
                  >
                    <div
                      className={`text-gray-600 dark:text-gray-300 font-medium ${formatted.line2 ? 'text-[8px]' : 'text-[10px]'}`}
                    >
                      <div className="whitespace-nowrap leading-tight">{formatted.line1}</div>
                      {formatted.line2 && (
                        <div className="whitespace-nowrap leading-tight">{formatted.line2}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Data rows */}
            {comparisonData.map((rowData, index) => (
              <div
                key={rowData.modelLabel}
                className={`flex items-center gap-0.5 px-2 border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${index % 2 === 0 ? 'bg-gray-25 dark:bg-gray-850' : 'bg-white dark:bg-gray-900'}`}
                style={{ height: '40px' }}
              >
                {filteredTimesteps.map((timestep) => {
                  const difference = rowData.timestepDifferences[timestep];
                  const isNA = difference === null || difference === undefined;
                  const { actualValue, baselineValue } = getValuesForTooltip(
                    rowData.modelLabel,
                    timestep,
                    typeof difference === 'number' ? difference : 0,
                    baselineLabel,
                    chartData,
                  );
                  const columnWidth = '50px';

                  return (
                    <div
                      key={timestep}
                      className={`flex-none h-6 flex items-center justify-center rounded text-xs font-semibold cursor-pointer transition-all hover:scale-105 hover:shadow-md ${isNA ? 'bg-gray-200 dark:bg-gray-600 border-2 border-dashed border-gray-400 dark:border-gray-500 text-gray-500 dark:text-gray-400' : `${getBaselineComparisonColor(difference as number, statisticalContext)} ${getBaselineComparisonTextColor(difference as number)}`}`}
                      onMouseEnter={(e) =>
                        onCellHover(
                          e,
                          rowData.modelLabel,
                          timestep,
                          typeof difference === 'number' ? difference : 0,
                          actualValue,
                          baselineValue,
                        )
                      }
                      onMouseLeave={onCellLeave}
                      style={{ minHeight: '24px', width: columnWidth }}
                    >
                      <span className="px-0.5 text-center">
                        {isNA
                          ? 'n/a'
                          : difference === 0
                            ? '0.0'
                            : `${(difference as number) > 0 ? '+' : ''}${(difference as number).toFixed(1)}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaselineComparisonHeatmapTable;
